import getLuxLoyaltyProductType from 'luxLoyalty/lib/getLuxLoyaltyProductType'
import getObjectKey from 'lib/object/getObjectKey'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { PRODUCT_TYPE_ULTRALUX } from 'constants/offer'
import getHotelEncryptedMargin from 'luxLoyalty/lib/margin/getHotelEncryptedMargin'

export function isHotelCalculationWithAdditionalOptions(
  options: App.LuxLoyaltyPointsCalculatorOptions,
): options is App.LuxLoyaltyHotelPointsCalculatorAdditionalOptions {
  return options.luxLoyaltyProductType === 'hotel_flash' || options.luxLoyaltyProductType === 'hotel_lpc'
}

/**
 * @returns formatted points key e.g. "price:100;productType:hotel;offerId:123;packageId:456"
 */
export function buildPointsKey(options: App.LuxLoyaltyPointsCalculatorOptions, memberId?: string): string {
  return getObjectKey({ ...options, memberId })
}

export function validateCalculatorOptions(options: App.LuxLoyaltyPointsCalculatorOptions): boolean {
  if (
    !Number.isFinite(options.price) ||
    !options.luxLoyaltyProductType ||
    (isHotelCalculationWithAdditionalOptions(options) && (!options.offerId || !options.packageId))
  ) {
    return false
  }

  return true
}

interface BaseAdditionalOptions {
  skip?: boolean;
}

interface BasePricingOptions {
  price?: number;
  memberPrice?: number;
}

interface FlightPricingOptions {
  flightPrice?: number;
}

interface GenerateLuxLoyaltyPointsCalculatorHotelAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  packageView?: OfferPackageOptionView;
  pkg?: App.HotelPackage;
}

export function generateLuxLoyaltyPointsCalculatorHotelOptions(
  offer: App.Offer | App.OfferSummary | undefined,
  options: GenerateLuxLoyaltyPointsCalculatorHotelAdditionalOptions,
): App.LuxLoyaltyPointsCalculatorOptions | undefined {
  const {
    packageView,
    pkg,
    skip,
    price,
    memberPrice,
  } = options
  let luxLoyaltyProductType: App.LuxLoyaltyProductType | undefined

  if (offer?.productType === PRODUCT_TYPE_ULTRALUX) {
    luxLoyaltyProductType = getLuxLoyaltyProductType(offer?.type)
  } else {
    luxLoyaltyProductType = getLuxLoyaltyProductType(offer?.productType)
  }

  if (
    !offer ||
    (!packageView && !pkg) ||
    (!luxLoyaltyProductType || (luxLoyaltyProductType !== 'hotel_flash' && luxLoyaltyProductType !== 'hotel_lpc')) ||
    skip
  ) return

  return {
    price: price ?? packageView?.pricing?.price ?? 0,
    memberPrice: memberPrice ?? packageView?.memberPricing?.price ?? 0,
    luxLoyaltyProductType,
    mx: getHotelEncryptedMargin(offer.type, packageView?.package ?? pkg),
    offerId: offer.id,
    packageId: packageView?.package.id ?? pkg?.id ?? '',
  }
}

interface GenerateLuxLoyaltyPointsCalculatorBedbankAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {}

export function generateLuxLoyaltyPointsCalculatorBedbankOptions(
  options: GenerateLuxLoyaltyPointsCalculatorBedbankAdditionalOptions | undefined,
): App.LuxLoyaltyPointsCalculatorOptions | undefined {
  const {
    skip,
    price,
    memberPrice,
  } = options ?? {}

  if (skip) return

  return {
    price: price ?? 0,
    memberPrice: memberPrice ?? 0,
    luxLoyaltyProductType: 'hotel_lpp_eps',
    mx: '',
  }
}

interface GenerateLuxLoyaltyPointsCalculatorFlightAdditionalOptions extends BaseAdditionalOptions, FlightPricingOptions {
  packageView?: OfferPackageOptionView;
}

export function generateLuxLoyaltyPointsCalculatorFlightOptions(
  offer: App.Offer | App.OfferSummary | undefined,
  options: GenerateLuxLoyaltyPointsCalculatorFlightAdditionalOptions,
): App.LuxLoyaltyPointsCalculatorOptions | undefined {
  const {
    skip,
    packageView,
    flightPrice,
  } = options ?? {}

  if (
    skip ||
    !offer?.offerFlightsEnabled
  ) return

  return {
    price: flightPrice ?? packageView?.flightPrice ?? 0,
    luxLoyaltyProductType: 'flight',
    mx: '',
  }
}

interface GenerateLuxLoyaltyPointsCalculatorInsuranceAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {}

export function generateLuxLoyaltyPointsCalculatorInsuranceOptions(
  quote: App.InsuranceQuote | undefined,
  options?: GenerateLuxLoyaltyPointsCalculatorInsuranceAdditionalOptions,
): App.LuxLoyaltyPointsCalculatorOptions | undefined {
  const {
    skip,
    price,
    memberPrice,
  } = options ?? {}

  if (skip) return

  return {
    price: price ?? quote?.total ?? 0,
    memberPrice: memberPrice ?? quote?.luxPlusTotal ?? 0,
    luxLoyaltyProductType: 'insurance',
  }
}
